import {
  Home,
  Search,
  Handshake,
  DollarSign,
  Megaphone,
  Activity,
  Users,
  BookOpen,
  Shield,
  Wrench,
  Bot,
  ShieldCheck,
  ClipboardCheck,
  Plug,
  MapPin,
  Database,
  ClipboardList,
  Wand2,
  Brain,
  History,
  Sparkles,
  Briefcase,
  Truck,
  Calendar,
  Building2,
  Network,
  Cpu,
  Radio,
  type LucideIcon,
} from 'lucide-react'
import { useCallback, useState } from 'react'
import { isEnabled, type FeatureFlag } from '@/lib/features'
import { isAgentShellPath } from '@/lib/agent-seats'

/** Dispatch on `window` to open the command palette from a click. */
export const OPEN_COMMAND_PALETTE_EVENT = 'forge:open-command-palette'

/**
 * Single source of truth for the primary nav — shared by the desktop
 * Sidebar and the MobileNav drawer so the two can't drift.
 */

export interface NavItem {
  label: string
  icon: LucideIcon
  to: string
  adminOnly?: boolean
  flag?: FeatureFlag
}

export interface NavGroup {
  /** Empty label = headerless block (Home/Approvals at the top). */
  label: string
  icon?: LucideIcon
  items: NavItem[]
  collapsible?: boolean
  defaultOpen?: boolean
}

/**
 * Ops room row, rendered above the Team section header and outside
 * its collapse toggle. Closing Team never hides this row. Desktop
 * sidebar and the phone drawer both render it. Its unread count is
 * the ops room's, never a seat's.
 */
export const OPS_NAV_ITEM: NavItem = {
  label: 'Ops',
  icon: Radio,
  to: '/ops',
}

export const NAV_GROUPS: NavGroup[] = [
  {
    label: '',
    items: [
      { label: 'Home', icon: Home, to: '/home' },
      { label: 'Approvals', icon: ClipboardCheck, to: '/approvals' },
    ],
  },
  {
    label: 'Pipeline',
    items: [
      { label: 'Leads', icon: Search, to: '/find' },
      { label: 'Listings', icon: Building2, to: '/listings' },
      { label: 'Parcels', icon: MapPin, to: '/parcels' },
      { label: 'Outreach', icon: Megaphone, to: '/outreach' },
      { label: 'Schedule', icon: Calendar, to: '/schedule' },
      { label: 'Opportunities', icon: Handshake, to: '/opportunities' },
    ],
  },
  {
    label: 'Network',
    icon: Network,
    collapsible: true,
    defaultOpen: false,
    items: [
      { label: 'Contacts', icon: Users, to: '/contacts' },
      { label: 'Buyers', icon: DollarSign, to: '/buyers' },
      { label: 'Agents', icon: Briefcase, to: '/agents' },
      { label: 'Wholesalers', icon: Truck, to: '/wholesalers' },
    ],
  },
  {
    label: 'Tools',
    icon: Plug,
    collapsible: true,
    defaultOpen: false,
    items: [
      { label: 'Research', icon: Sparkles, to: '/research', flag: 'research' },
      { label: 'Integrations', icon: Plug, to: '/integrations' },
      { label: 'Data Sources', icon: Database, to: '/data-sources' },
    ],
  },
  {
    label: 'AI Systems',
    icon: Cpu,
    collapsible: true,
    defaultOpen: false,
    items: [
      { label: 'Plans', icon: ClipboardList, to: '/plans', flag: 'plans' },
      { label: 'Swarms', icon: Users, to: '/swarms', flag: 'swarms' },
      { label: 'Skills', icon: Wand2, to: '/skills', flag: 'skills' },
      { label: 'Costs', icon: DollarSign, to: '/costs', flag: 'costs' },
      { label: 'Memory', icon: Brain, to: '/memory', flag: 'memory' },
      { label: 'Sessions', icon: History, to: '/sessions', flag: 'sessions' },
    ],
  },
  {
    label: 'Admin',
    icon: Shield,
    collapsible: true,
    defaultOpen: false,
    items: [
      { label: 'Admin Settings', icon: Shield, to: '/admin', adminOnly: true },
      { label: 'Knowledge Base', icon: BookOpen, to: '/knowledge', adminOnly: true },
      { label: 'Tool Store', icon: Wrench, to: '/tool-store', adminOnly: true, flag: 'toolStore' },
      { label: 'Parcel Gen', icon: Database, to: '/parcel-gen', adminOnly: true, flag: 'parcelGen' },
      { label: 'Parcel Data', icon: Database, to: '/parcel-data', adminOnly: true, flag: 'parcelData' },
      { label: 'Agent Settings', icon: Bot, to: '/settings/agents', adminOnly: true, flag: 'agentSettings' },
      { label: 'Guardrails', icon: ShieldCheck, to: '/guardrails', adminOnly: true, flag: 'guardrails' },
      { label: 'Operations', icon: Activity, to: '/operations', adminOnly: true, flag: 'operations' },
    ],
  },
]

/**
 * Drop items whose feature flag is off and admin-only items for
 * non-admins, then drop groups that end up empty.
 */
export function visibleNavGroups(isAdmin: boolean, groups: NavGroup[] = NAV_GROUPS): NavGroup[] {
  return groups
    .map((group) => ({
      ...group,
      items: group.items.filter(
        (item) => (!item.flag || isEnabled(item.flag)) && (!item.adminOnly || isAdmin),
      ),
    }))
    .filter((group) => group.items.length > 0)
}

/** True when `pathname` is (or is nested under) one of the group's routes. */
export function groupContainsPath(group: NavGroup, pathname: string): boolean {
  // `/agents/scout` is a team seat, not the realtor directory at `/agents`.
  if (isAgentShellPath(pathname)) return false
  return group.items.some((item) => pathname === item.to || pathname.startsWith(`${item.to}/`))
}

// ── Persisted open/closed state ────────────────────────────────────────

const OPEN_KEY = 'forge-nav-open'

export function readNavOpenState(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(OPEN_KEY)
    const parsed = raw ? JSON.parse(raw) : null
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

export function writeNavOpenState(state: Record<string, boolean>) {
  try {
    localStorage.setItem(OPEN_KEY, JSON.stringify(state))
  } catch {
    /* storage unavailable — state just won't persist */
  }
}

/**
 * Open/closed state for nav sections, keyed by label. Persists across
 * reloads, and force-opens whichever section holds the current route so
 * the active item is never hidden inside a closed section.
 */
export function useNavSections(pathname: string, groups: NavGroup[] = []) {
  const [open, setOpen] = useState<Record<string, boolean>>(readNavOpenState)

  const activeLabel = groups.find((g) => g.collapsible && groupContainsPath(g, pathname))?.label
  // Adjust-state-during-render: when the route moves into a closed
  // section, open it once (the user can still close it afterwards).
  const [openedFor, setOpenedFor] = useState<string | undefined>(undefined)
  if (activeLabel !== openedFor) {
    setOpenedFor(activeLabel)
    if (activeLabel && !open[activeLabel]) setOpen({ ...open, [activeLabel]: true })
  }

  const isOpen = useCallback(
    (label: string, defaultOpen = true) => open[label] ?? defaultOpen,
    [open],
  )
  const toggle = useCallback((label: string, defaultOpen = true) => {
    setOpen((prev) => {
      const next = !(prev[label] ?? defaultOpen)
      // Merge per key — Sidebar and TeamSeatLinks each hold an instance.
      writeNavOpenState({ ...readNavOpenState(), [label]: next })
      return { ...prev, [label]: next }
    })
  }, [])

  return { isOpen, toggle }
}
